import axios from 'axios'
import { UnexpectedApiResponseError } from 'src/Libs/errors'
import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { IdlePeriodSettingsFormModel, IdlePeriodSettingsModel } from 'src/Libs/smart-charge/models'
import { IdlePeriodsSettingsDto } from 'src/Libs/smart-charge/dto'
import { fromIdlePeriodDtoToModel, fromIdlePeriodSettingsFormToUpdateDto } from 'src/Libs/smart-charge/transformers'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'

interface ApiResponseError {
  statusCode: StatusCodes
  errno: number
  error: string
}

/**
 * Returns the default idle period settings for a storage reference.
 *
 * Extends the returning ApiResult object with the information, if a compatibility error appeared.
 *
 * This error-information is important to differ between using this API or the legacy API.
 *
 * @param serialNumber
 * @param authFetch
 */
export const getSettings = async (
  serialNumber: string,
  authFetch: AuthFetchWithResponse,
  enabled: boolean
): Promise<ApiResult<IdlePeriodSettingsModel, boolean>> => {
  if (!enabled) return new ApiResult<IdlePeriodSettingsModel, boolean>(null, false, false)

  try {
    const response = await authFetch<IdlePeriodsSettingsDto | ApiResponseError>({
      path: `storages/${serialNumber}/smart-functions/smart-charge/idle-periods`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const model = fromIdlePeriodDtoToModel(response.data as IdlePeriodsSettingsDto)
      return new ApiResult<IdlePeriodSettingsModel, boolean>(model, false, false)
    }
    return new ApiResult<IdlePeriodSettingsModel, boolean>(null, true, false)
  } catch (error) {
    const defaultError = new ApiResult<IdlePeriodSettingsModel, boolean>(null, true, false)
    if (!axios.isAxiosError(error)) {
      return defaultError
    }

    const err = error as AuthFetchError

    if (err.response?.status === StatusCodes.BAD_REQUEST && (err.response?.data as ApiResponseError).errno === FetchErrorCodes.NotSupported) {
      return new ApiResult<IdlePeriodSettingsModel, boolean>(null, true, true)
    }

    return defaultError
  }
}

/**
 * Updates the default idle period settings for a storage reference.
 *
 * @param serialNumber
 * @param model
 * @param authFetch
 */
export const updateSettings = async (serialNumber: string, model: IdlePeriodSettingsFormModel, authFetch: AuthFetchWithResponse): Promise<void> => {
  const payload = fromIdlePeriodSettingsFormToUpdateDto(model)
  const response = await authFetch<void>({
    path: `storages/${serialNumber}/smart-functions/smart-charge/idle-periods`,
    method: 'PUT',
    payload: JSON.stringify(payload),
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
}
